import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, Button, Divider } from '@mui/material';
import { ArrowBack, Edit, Inventory, LocationOn, Assignment, AttachMoney, Business } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import materialsService, { Material as MaterialType } from '../services/materialsService';

const formatCurrency = (val?: number) => typeof val === 'number' ? `$${val.toLocaleString()}` : '-';

const deriveUnitCost = (item: any) => (item.unitCost ?? item.pricing?.unitCost ?? 0);
const deriveSupplierName = (item: any) => {
  const primary = item?.supplier?.primary;
  if (primary && typeof primary === 'object') return primary.name || '—';
  if (typeof item?.supplier === 'string') return item.supplier;
  return '—';
};
const deriveLocationText = (item: any) => {
  const loc = item?.location;
  if (typeof loc === 'string') return loc;
  if (loc && typeof loc === 'object') {
    const parts = [loc.warehouse, loc.zone, loc.aisle, loc.shelf].filter(Boolean).map((p: string) => p.trim());
    return parts.length ? parts.join(' / ') : '—';
  }
  return '—';
};
const normalizeStatus = (raw?: string) => {
  const s = String(raw || '').toLowerCase();
  if (s === 'active') return 'Active';
  if (s === 'backordered') return 'Backordered';
  if (s === 'out of stock') return 'Out of Stock';
  if (s === 'discontinued') return 'Discontinued';
  return raw || 'Active';
};

const MaterialDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState<MaterialType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        if (!id) throw new Error('Invalid material id');
        const data = await materialsService.getById(id);
        setItem(data);
      } catch (e: any) {
        setError(e?.message || 'Failed to load material');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading material...</Typography>
      </Box>
    );
  }

  if (error || !item) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/materials')} sx={{ mb: 2 }}>Back</Button>
        <Typography variant="h6" color="error">{error || 'Material not found'}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/materials')} sx={{ mr: 2 }}>Back</Button>
        <Typography variant="h4" sx={{ flex: 1 }}>Material Details</Typography>
        <Button variant="outlined" startIcon={<Edit />}>Edit</Button>
      </Box>

      {/* Summary Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Inventory color="primary" sx={{ mr: 2 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">{item.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {item.category || '-'} • Unit: {item.unit || '-'}
              </Typography>
            </Box>
            <Chip label={normalizeStatus((item as any).status)} color="primary" />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assignment color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Current Stock</Typography>
                  <Typography variant="body2">{(item as any).currentStock ?? (item as any).currentQuantity ?? 0}</Typography>
                </Box>
              </Box>
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Unit Cost</Typography>
                  <Typography variant="body2">{formatCurrency(deriveUnitCost(item))}</Typography>
                </Box>
              </Box>
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Supplier</Typography>
                  <Typography variant="body2">{deriveSupplierName(item)}</Typography>
                </Box>
              </Box>
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Location</Typography>
                  <Typography variant="body2">{deriveLocationText(item)}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Project Allocations */}
      {Array.isArray((item as any).projects) && (item as any).projects.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Project Allocations</Typography>
            {(item as any).projects.map((p: any, idx: number) => (
              <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{p.project?.name || 'Unknown Project'}</Typography>
                <Typography variant="body2">Allocated: {p.allocatedQuantity ?? 0} • Used: {p.usedQuantity ?? 0}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Specifications */}
      {item.specifications && Object.keys(item.specifications).length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Specifications</Typography>
            {Object.entries(item.specifications).map(([key, value]) => (
              <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{key}</Typography>
                <Typography variant="body2">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MaterialDetail;